import { AppBar, Toolbar, Box, Button, IconButton } from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import SearchIcon from "@material-ui/icons/Search";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ViewKanbanIcon from "@material-ui/icons/ViewColumn";
import NotificationsIcon from "@material-ui/icons/Notifications";
import EventNoteIcon from "@material-ui/icons/EventNote";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import { Link } from "@backstage/core-components";
import { UserSettingsSignInAvatar } from "@backstage/plugin-user-settings";
import HomeIcon from "@material-ui/icons/Home";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { useRootStyles } from "../styles/useRootStyles";
import {
  AgentPoliciesNavItem,
  ForgeNavItem,
  PermissionNavItem,
  ToolsDropdown,
  StanNavItem,
  CrmlyNavItem,
} from "./NavItems";
import {
  apiDocsAccessPermission,
  docsAccessPermission,
  createAccessPermission,
} from "../../../permissions";
import ExtensionIcon from "@material-ui/icons/Extension";
import LibraryBooks from "@material-ui/icons/LibraryBooks";
import CreateComponentIcon from "@material-ui/icons/AddCircleOutline";
import LogoFull from "../LogoFull";
import { AppContext } from "../hooks/useAppContext";
import { NAV_COLORS } from "../styles/useRootStyles";
import { usePermission } from "@backstage/plugin-permission-react";
import { crmonCommissionsPermission } from "@internal/plugin-crmon";

interface AppBarNavProps {
  onDrawerToggle: () => void;
  onSearchOpen: () => void;
  stanDisplayName: string;
  appContext: AppContext;
}

const CrmlyNav = ({
  classes,
}: {
  classes: ReturnType<typeof useRootStyles>;
}) => {
  const { allowed: commissionsAllowed } = usePermission({
    permission: crmonCommissionsPermission,
  });

  return (
    <>
      <Button
        component={Link}
        to="/"
        className={classes.backButton}
        startIcon={<ArrowBackIcon style={{ fontSize: 14 }} />}
        color="inherit"
      >
        Portal
      </Button>
      <Button
        component={Link}
        to="/crmon"
        className={classes.navButton}
        startIcon={<ViewKanbanIcon />}
        color="inherit"
      >
        Pipeline
      </Button>
      <Button
        component={Link}
        to="/crmon/nudges"
        className={classes.navButton}
        startIcon={<NotificationsIcon />}
        color="inherit"
      >
        Nudges
      </Button>
      <Button
        component={Link}
        to="/crmon/weekly-sync"
        className={classes.navButton}
        startIcon={<EventNoteIcon />}
        color="inherit"
      >
        Weekly Sync
      </Button>
      {commissionsAllowed && (
        <Button
          component={Link}
          to="/crmon/commissions"
          className={classes.navButton}
          startIcon={<AttachMoneyIcon />}
          color="inherit"
        >
          Commissions
        </Button>
      )}
    </>
  );
};

export const AppBarNav = ({
  onDrawerToggle,
  onSearchOpen,
  stanDisplayName,
  appContext,
}: AppBarNavProps) => {
  const classes = useRootStyles();

  return (
    <AppBar
      position="static"
      className={classes.appBar}
      elevation={0}
      // eslint-disable-next-line security/detect-object-injection
      style={{ backgroundColor: NAV_COLORS[appContext] }}
    >
      <Toolbar className={classes.toolbar}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={onDrawerToggle}
            className={classes.hamburgerButton}
            edge="start"
          >
            <MenuIcon />
          </IconButton>

          {appContext === "crmon" ? (
            <Link to="/crmon" underline="none">
              <Box className={classes.crmonLogoWrapper}>
                <img
                  src="/leapstaq-crmon-mascot.png"
                  alt="CRMon"
                  className={classes.crmonLogo}
                />
              </Box>
            </Link>
          ) : (
            <Link to="/" underline="none">
              <Box className={classes.logo}>
                <LogoFull />
              </Box>
            </Link>
          )}

          <Box className={classes.navButtons}>
            {appContext === "crmon" ? (
              <CrmlyNav classes={classes} />
            ) : (
              <>
                <Button
                  component={Link}
                  to="/"
                  className={classes.navButton}
                  startIcon={<HomeIcon />}
                  color="inherit"
                >
                  Home
                </Button>
                <ForgeNavItem />
                <PermissionNavItem
                  to="api-docs"
                  icon={ExtensionIcon}
                  label="APIs"
                  permission={apiDocsAccessPermission}
                />
                <PermissionNavItem
                  to="docs"
                  icon={LibraryBooks}
                  label="Docs"
                  permission={docsAccessPermission}
                />
                <PermissionNavItem
                  to="create"
                  icon={CreateComponentIcon}
                  label="Create"
                  permission={createAccessPermission}
                />
                <Button
                  component={Link}
                  to="odoo-timesheet"
                  className={classes.navButton}
                  startIcon={<AccessTimeIcon />}
                  color="inherit"
                >
                  Timewriting
                </Button>
                <ToolsDropdown />
                <AgentPoliciesNavItem />
                <StanNavItem displayName={stanDisplayName} />
                <CrmlyNavItem />
              </>
            )}
          </Box>
        </Box>
        <Box className={classes.rightSection}>
          <IconButton onClick={onSearchOpen} className={classes.iconButton}>
            <SearchIcon />
          </IconButton>
          <IconButton
            component={Link}
            to="/settings"
            className={classes.iconButton}
          >
            <UserSettingsSignInAvatar />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
